import { CustomMenu, useMenuState, MenuItemInternalLink, ActionButton, ActionButtonType, Divider, NavigationButton, NavigationButtonType } from "@octopusdeploy/design-system-components";
import type { ResourcesById, EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import { Permission, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import type { ArrayAtLeastLength } from "@octopusdeploy/type-utils";
import { isArrayLengthAtLeast } from "@octopusdeploy/type-utils";
import * as React from "react";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import type { ActionEvent } from "~/analytics/Analytics";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { repository, session } from "~/clientInstance";
import { DropDownIcon } from "~/components/Button/DropDownIcon/DropDownIcon";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import { useOctopusTheme } from "~/components/Theme/index";
import styles from "./DeployButton.module.less";
export interface DeployButtonProps {
    spaceId: string;
    projectSlug: string;
    projectId: string;
    releaseVersion: string;
    nextDeployments: string[];
    environmentsById: ResourcesById<EnvironmentResource>;
    tenantedDeploymentMode: TenantedDeploymentMode;
}
export function DeployButton({ nextDeployments = [], tenantedDeploymentMode, environmentsById, projectSlug, releaseVersion, projectId, spaceId }: DeployButtonProps) {
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    const dispatchDeployReleaseAnalyticsEvent = useCallback(() => {
        const ev: ActionEvent = {
            action: Action.Deploy,
            resource: "Deploy Release",
        };
        dispatchAction("Initiate Deployment", ev);
    }, [dispatchAction]);
    const getEnvironmentLink = useGetEnvironmentLink(spaceId, projectSlug, releaseVersion);
    const visibleEnvironments = useVisibleEnvironments(environmentsById, projectId, nextDeployments);
    if (isArrayLengthAtLeast(visibleEnvironments, 2)) {
        return (<DeployToMultipleEnvironmentsButton tenantedDeploymentMode={tenantedDeploymentMode} getEnvironmentLink={getEnvironmentLink} visibleEnvironments={visibleEnvironments} dispatchDeployReleaseAnalyticsEvent={dispatchDeployReleaseAnalyticsEvent} environmentsById={environmentsById}/>);
    }
    else if (visibleEnvironments.length === 1) {
        const singleEnvironment = visibleEnvironments[0];
        const environment = environmentsById[singleEnvironment];
        return <NavigationButton type={NavigationButtonType.Primary} href={getEnvironmentLink([singleEnvironment])} label={`Deploy to ${environment ? environment.Name : ""}...`} onClick={dispatchDeployReleaseAnalyticsEvent}/>;
    }
    return null;
}
function useGetEnvironmentLink(spaceId: string, projectSlug: string, releaseVersion: string): (environmentId: string[] | undefined) => LinkHref {
    return useCallback((environments: string[] | undefined) => {
        return links.createDeploymentPage.generateUrl({ spaceId, projectSlug, releaseVersion }, { environmentIds: environments && environments.length > 0 ? environments : undefined });
    }, [spaceId, projectSlug, releaseVersion]);
}
function useVisibleEnvironments(environmentsById: ResourcesById<EnvironmentResource>, projectId: string, nextDeployments: string[]) {
    return useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const isAuthorizedToCreateDeployment = (environmentId: string) => session.currentPermissions!.scopeToSpace(repository.spaceId).isAuthorized({ permission: Permission.DeploymentCreate, environmentId, projectId: projectId, tenantId: "*" });
        const isKnownEnvironment = (environmentId: string) => environmentsById.hasOwnProperty(environmentId);
        return nextDeployments.filter(isKnownEnvironment).filter(isAuthorizedToCreateDeployment);
    }, [environmentsById, nextDeployments, projectId]);
}
interface DeployToMultipleEnvironmentsButtonProps {
    dispatchDeployReleaseAnalyticsEvent: () => void;
    tenantedDeploymentMode: TenantedDeploymentMode;
    visibleEnvironments: ArrayAtLeastLength<string, 2>;
    getEnvironmentLink: (environment: string[] | undefined) => LinkHref;
    environmentsById: ResourcesById<EnvironmentResource>;
}
function DeployToMultipleEnvironmentsButton({ dispatchDeployReleaseAnalyticsEvent, tenantedDeploymentMode, visibleEnvironments, getEnvironmentLink, environmentsById }: DeployToMultipleEnvironmentsButtonProps) {
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const [filter, setFilter] = useState("");
    const firstFilteredOptionRef = useRef<HTMLAnchorElement | null>(null);
    const onKeyDown = useCallback((ev: KeyboardEvent) => {
        switch (ev.key) {
            case "Tab":
                ev.preventDefault();
                firstFilteredOptionRef.current?.focus();
                break;
            case "ArrowDown":
                firstFilteredOptionRef.current?.focus();
                break;
        }
    }, []);
    const filteredEnvironments = useMemo(() => visibleEnvironments.filter((id) => environmentsById[id].Name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())), [environmentsById, filter, visibleEnvironments]);
    const canDeployToMultipleEnvironments = tenantedDeploymentMode !== TenantedDeploymentMode.Tenanted;
    return (<>
            <ActionButton type={ActionButtonType.Primary} icon={<DropDownIcon />} iconPosition="right" label="Deploy to..." onClick={openMenu} menuButtonAttributes={buttonAriaAttributes}/>
            <CustomMenu accessibleName={"Deploy release"} menuState={menuState}>
                {canDeployToMultipleEnvironments && (<>
                        <DeployToAllAvailableEnvironmentsMenuItem filteredEnvironments={filteredEnvironments} getEnvironmentLink={getEnvironmentLink} onClick={dispatchDeployReleaseAnalyticsEvent}/>
                        <Divider />
                        <EnvironmentsFilter filter={filter} setFilter={setFilter} autoFocus={true} onKeyDown={onKeyDown}/>
                    </>)}
                {filteredEnvironments.map((e, index) => {
            const environment = environmentsById[e];
            return <MenuItemInternalLink key={e} path={getEnvironmentLink([e])} label={environment.Name} onClick={dispatchDeployReleaseAnalyticsEvent} size={"compact"} ref={index === 0 ? firstFilteredOptionRef : undefined}/>;
        })}
            </CustomMenu>
        </>);
}
interface DeployToAllAvailableEnvironmentsMenuItemProps {
    filteredEnvironments: string[];
    getEnvironmentLink: (environment: string[] | undefined) => LinkHref;
    onClick: () => void;
}
// The forwardRef here is not important, but needed to prevent console errors that would otherwise say a ref hasn't been appropriately forwarded.
// This requirement has been removed in later versions of material-ui (v5 onwards) and doesn't remove any functionality given the way we are using these components.
// See https://github.com/mui-org/material-ui/pull/25691/files#diff-ad4b8459eb1cd3c5e6882eb705e3e341e551bd7bf4ab9a6941ef12017eb1cb06L177-L188
const DeployToAllAvailableEnvironmentsMenuItem = forwardRef(({ filteredEnvironments, getEnvironmentLink, onClick }: DeployToAllAvailableEnvironmentsMenuItemProps, ref: unknown) => {
    const label = filteredEnvironments.length === 2 ? "Deploy to both environments..." : `Deploy to all ${filteredEnvironments.length} environments...`;
    return <MenuItemInternalLink label={label} path={getEnvironmentLink(filteredEnvironments)} onClick={onClick} size={"compact"}/>;
});
interface EnvironmentsFilterProps {
    filter: string;
    setFilter: (newFilter: string) => void;
    autoFocus: boolean;
    onKeyDown: (event: KeyboardEvent) => void;
}
function EnvironmentsFilter({ filter, setFilter, autoFocus, onKeyDown }: EnvironmentsFilterProps) {
    const theme = useOctopusTheme();
    return (<div className={styles.deployButtonFilterContainer}>
            <FilterSearchBox autoFocus={autoFocus} value={filter} placeholder="Filter..." onKeyDown={(e) => {
            //There is special handling for keydown events in menu items so we have to stop this from propagating.
            e.stopPropagation();
            onKeyDown(e);
        }} onChange={setFilter} fullWidth={true} containerClassName={styles.filterFieldContainer} iconColor={theme.whiteConstant}/>
        </div>);
}
